import { useCallback, useMemo } from "react";
import { Modal } from "antd";
import { STATUS } from "./harvestEntryConstants.js";

export function useHarvestEntryBulkActions({
  selectedRowIds,
  rowsById,
  getBackendStatus,
  rowHasPendingChanges,
  ensurePendingDate,
  showWeight,
  pushUndoSnapshot,
  rowsRef,
  setRows,
  clearRowSelection,
  messageApi,
}) {
  const bulkEligibleCounts = useMemo(() => {
    const counts = {
      mark_harvested: 0,
      mark_discarded: 0,
      mark_unharvested: 0,
      revert_to_saved: 0,
    };

    for (const id of selectedRowIds) {
      const row = rowsById.get(id);
      if (!row) continue;

      const backend = getBackendStatus(row);

      if (backend === STATUS.PLANTED) counts.mark_harvested += 1;
      if (backend === STATUS.PLANTED || backend === STATUS.HARVESTED)
        counts.mark_discarded += 1;
      if (backend === STATUS.HARVESTED || backend === STATUS.DISCARDED)
        counts.mark_unharvested += 1;

      if (rowHasPendingChanges(row)) counts.revert_to_saved += 1;
    }

    return counts;
  }, [selectedRowIds, rowsById, getBackendStatus, rowHasPendingChanges]);

  const confirmBulkAction = useCallback(
    ({ title, affectedCount, onConfirm }) => {
      Modal.confirm({
        title,
        content: `${affectedCount} row(s) will be affected.`,
        okText: "Confirm",
        cancelText: "Cancel",
        centered: true,
        onOk: onConfirm,
      });
    },
    [],
  );

  const handleBulkMarkHarvested = useCallback(() => {
    if (!selectedRowIds.size) {
      messageApi.warning("Select at least 1 row to use Bulk Actions.");
      return;
    }

    const eligibleIds = new Set();
    for (const id of selectedRowIds) {
      const row = rowsById.get(id);
      if (!row) continue;
      if (getBackendStatus(row) === STATUS.PLANTED) eligibleIds.add(id);
    }

    if (!eligibleIds.size) {
      messageApi.info("No selected rows are eligible for: Mark as Harvested.");
      return;
    }

    confirmBulkAction({
      title: "Mark as Harvested?",
      affectedCount: eligibleIds.size,
      onConfirm: () => {
        pushUndoSnapshot(rowsRef.current);
        setRows((prev) =>
          prev.map((row) => {
            if (!eligibleIds.has(row.id)) return row;

            const base = {
              ...row,
              pendingUnharvest: false,
              pendingYield: null,
              pendingWeight: null,
              pendingPloughedReasons: null,
              pendingPloughedNote: null,
            };
            return ensurePendingDate(base);
          }),
        );

        clearRowSelection();

        messageApi.success(
          `Marked ${eligibleIds.size} row(s) as harvested. Remember to save.`,
        );
      },
    });
  }, [
    clearRowSelection,
    confirmBulkAction,
    ensurePendingDate,
    getBackendStatus,
    messageApi,
    pushUndoSnapshot,
    rowsById,
    rowsRef,
    selectedRowIds,
    setRows,
  ]);

  const handleBulkMarkDiscarded = useCallback(() => {
    if (!selectedRowIds.size) {
      messageApi.warning("Select at least 1 row to use Bulk Actions.");
      return;
    }

    const eligibleIds = new Set();
    for (const id of selectedRowIds) {
      const row = rowsById.get(id);
      if (!row) continue;
      const backend = getBackendStatus(row);
      if (backend === STATUS.PLANTED || backend === STATUS.HARVESTED) {
        eligibleIds.add(id);
      }
    }

    if (!eligibleIds.size) {
      messageApi.info("No selected rows are eligible for: Mark as Discarded.");
      return;
    }

    confirmBulkAction({
      title: "Mark as Discarded?",
      affectedCount: eligibleIds.size,
      onConfirm: () => {
        pushUndoSnapshot(rowsRef.current);
        setRows((prev) =>
          prev.map((row) => {
            if (!eligibleIds.has(row.id)) return row;

            const base = {
              ...row,
              pendingUnharvest: false,
              pendingYield: -1,
              pendingWeight: showWeight ? -1 : row.pendingWeight,
              pendingPloughedReasons: null,
              pendingPloughedNote: null,
            };
            return ensurePendingDate(base);
          }),
        );

        clearRowSelection();

        messageApi.success(
          `Marked ${eligibleIds.size} row(s) as discarded. Remember to save.`,
        );
      },
    });
  }, [
    clearRowSelection,
    confirmBulkAction,
    ensurePendingDate,
    getBackendStatus,
    messageApi,
    pushUndoSnapshot,
    rowsById,
    rowsRef,
    selectedRowIds,
    setRows,
    showWeight,
  ]);

  const handleBulkMarkUnharvested = useCallback(() => {
    if (!selectedRowIds.size) {
      messageApi.warning("Select at least 1 row to use Bulk Actions.");
      return;
    }

    const eligibleIds = new Set();
    for (const id of selectedRowIds) {
      const row = rowsById.get(id);
      if (!row) continue;
      const backend = getBackendStatus(row);
      if (backend === STATUS.HARVESTED || backend === STATUS.DISCARDED) {
        eligibleIds.add(id);
      }
    }

    if (!eligibleIds.size) {
      messageApi.info(
        "No selected rows are eligible for: Mark as Unharvested.",
      );
      return;
    }

    confirmBulkAction({
      title: "Mark as Unharvested?",
      affectedCount: eligibleIds.size,
      onConfirm: () => {
        pushUndoSnapshot(rowsRef.current);
        setRows((prev) =>
          prev.map((row) => {
            if (!eligibleIds.has(row.id)) return row;

            return {
              ...row,
              pendingUnharvest: true,
              pendingHarvestDate: null,
              pendingYield: null,
              pendingWeight: null,
              pendingTubWeight: null,
              pendingPloughedReasons: null,
              pendingPloughedNote: null,
            };
          }),
        );

        clearRowSelection();
        messageApi.success(
          `Marked ${eligibleIds.size} row(s) as unharvested. Remember to save.`,
        );
      },
    });
  }, [
    clearRowSelection,
    confirmBulkAction,
    getBackendStatus,
    messageApi,
    pushUndoSnapshot,
    rowsById,
    rowsRef,
    selectedRowIds,
    setRows,
  ]);

  const handleBulkRevertToSaved = useCallback(() => {
    if (!selectedRowIds.size) {
      messageApi.warning("Select at least 1 row to use Bulk Actions.");
      return;
    }

    const eligibleIds = new Set();
    for (const id of selectedRowIds) {
      const row = rowsById.get(id);
      if (!row) continue;
      if (rowHasPendingChanges(row)) eligibleIds.add(id);
    }

    if (!eligibleIds.size) {
      messageApi.info("No selected rows have unsaved changes to revert.");
      return;
    }

    confirmBulkAction({
      title: "Revert to Saved?",
      affectedCount: eligibleIds.size,
      onConfirm: () => {
        pushUndoSnapshot(rowsRef.current);
        setRows((prev) =>
          prev.map((row) => {
            if (!eligibleIds.has(row.id)) return row;
            return {
              ...row,
              pendingUnharvest: false,
              pendingHarvestDate: null,
              pendingYield: null,
              pendingWeight: null,
              pendingTubWeight: null,
              pendingPloughedReasons: null,
              pendingPloughedNote: null,
            };
          }),
        );

        clearRowSelection();
        messageApi.success(
          `Reverted ${eligibleIds.size} row(s) to saved values.`,
        );
      },
    });
  }, [
    clearRowSelection,
    confirmBulkAction,
    messageApi,
    pushUndoSnapshot,
    rowsById,
    rowsRef,
    rowHasPendingChanges,
    selectedRowIds,
    setRows,
  ]);

  const bulkMenuItems = useMemo(
    () => [
      {
        key: "mark_harvested",
        label: "Mark as Harvested",
        disabled: bulkEligibleCounts.mark_harvested === 0,
        onClick: handleBulkMarkHarvested,
      },
      {
        key: "mark_discarded",
        label: "Mark as Discarded",
        disabled: bulkEligibleCounts.mark_discarded === 0,
        onClick: handleBulkMarkDiscarded,
      },
      {
        key: "mark_unharvested",
        label: "Mark as Unharvested",
        disabled: bulkEligibleCounts.mark_unharvested === 0,
        onClick: handleBulkMarkUnharvested,
      },
      {
        key: "revert_to_saved",
        label: "Revert to Saved",
        disabled: bulkEligibleCounts.revert_to_saved === 0,
        onClick: handleBulkRevertToSaved,
      },
    ],
    [
      bulkEligibleCounts,
      handleBulkMarkDiscarded,
      handleBulkMarkHarvested,
      handleBulkMarkUnharvested,
      handleBulkRevertToSaved,
    ],
  );

  return {
    bulkEligibleCounts,
    bulkMenuItems,
    handleBulkMarkHarvested,
    handleBulkMarkDiscarded,
    handleBulkMarkUnharvested,
    handleBulkRevertToSaved,
  };
}
